import { useCloseVesselAlert } from '../hooks/useVesselChanges';
import {
  vesselAlertArrival,
  vesselAlertJobLink,
  vesselAlertPort,
  vesselAlertPortText,
  vesselAlertTerminal,
  vesselAlertTone,
} from '../model/vesselAlerts';
import type { VesselAlert } from '../types/vesselChanges';
import { AlertActionButton, AlertRowDetails, AlertRowTitle } from './VesselAlertParts';

interface VesselLinkAlertRowProps {
  alert: VesselAlert;
  onOpen: (path: string) => void;
}

/**
 * "MAERSK NOBERG → 642N, ETA 19 Oct 00:00 (est.)", or the vessel and "No PTP voyage suggested"
 * ("Westports" for a job at Westports, "MMSW · Northport KCT" for one at an MMSW terminal), then the job's
 * Westports terminal if known.
 */
const suggestion = (alert: VesselAlert): string[] => {
  const arrival = vesselAlertArrival(alert);
  const parts = alert.voyage
    ? [`${alert.vesselName} → ${alert.voyage}${arrival ? `, ETA ${arrival}` : ''}`]
    : [alert.vesselName, `No ${vesselAlertPortText(alert)} voyage suggested`];
  const terminal = vesselAlertTerminal(alert);
  return terminal ? [...parts, terminal] : parts;
};

/**
 * A job at PTP whose vessel is not linked to a PTP voyage, while PTP lists a vessel with that name:
 * open the job to pick the voyage, or say "Not this vessel" to close the alert for good.
 */
export const VesselLinkAlertRow = ({ alert, onOpen }: VesselLinkAlertRowProps) => {
  const close = useCloseVesselAlert();
  const jobLink = vesselAlertJobLink(alert);

  return (
    <li data-alert={alert.id} data-kind="LINK" className={`border-l-2 px-3 py-2 ${vesselAlertTone(alert)}`}>
      <AlertRowTitle alert={alert}>{`Pick ${vesselAlertPort(alert)} voyage · job ${alert.jobNo ?? ''}`.trim()}</AlertRowTitle>
      <AlertRowDetails alert={alert} parts={suggestion(alert)} />
      <div className="-ml-1 mt-1 flex gap-2">
        {jobLink && <AlertActionButton label="Open job" onClick={() => onOpen(jobLink)} />}
        <AlertActionButton label="Not this vessel" disabled={close.isPending} onClick={() => close.mutate(alert.id)} />
      </div>
    </li>
  );
};
